import type { BadgeLocationData } from '../badges/badge-location-data';
import type { EventType } from '../badges/event';
import { FacilitySettings, WaltSettings } from '../facility/facility';
import { LocationType } from '../location/location-update';
import { Point } from '../location/point';
import { MeshRepeater } from '../mesh/mesh-repeater';
import { i18nLocale } from '../translate/translate';
import { LogRequest } from '../weavix-support/log-request';
import { SimInfo, WifiInfo } from './walt-connect-info';

export interface WranglerPlacement {
    id?: string;
    slot?: string;
    date: Date;
}

/** Why a walt powered off, as reported by the walt in its shutdown message. */
export type PoweredOffReason = 'user' | 'device-management' | 'battery' | 'too-hot' | 'too-cold';

export interface WaltUpdate {
    id?: string; // serialNumber
    accountId?: string;
    location?: Point;
    level?: number;
    locationAccuracy?: number;
    locationType?: LocationType;
    locationUpdated?: Date;
    lastAssigned?: {
        accountId?: string;
        accountName?: string;
        personId?: string;
        personName?: string;
        companyId?: string;
        companyName?: string;
        date?: Date;
        facilityId?: string;

        // filled in fields
        facilityName?: string;
        facilityLocation?: Point;
    };
    network?: string;
    battery?: number;
    version?: string;

    /**@deprecated use simOne & simTwo */
    imei?: string;
    /**@deprecated use simOne & simTwo */
    iccid?: string;

    /**Preferred slot*/
    simOne?: SimInfo;
    /**Other slot*/
    simTwo?: SimInfo;
    /**MAC Address */
    macAddress?: string;
    /**Bluetooth address */
    btAddress?: string;
    /**Current wifi network*/
    currentNetwork?: WifiInfo;

    loggedIn?: Date;
    remoteLogout?: Date;
    updated?: Date;
    /**
     * Set by backend when an on-demand refresh request times out.
     * If this timestamp is newer than the ( lastReportedAt ?? updated ) timestamp, the device is disconnected.
     */
    refreshFailedAt?: Date;
    /**
     * Server time of the walt's most recent direct report; Console/admin edits, MQTT disconnects and mesh relays never move it.
     * A failed refresh seeds it once from `updated` when it's missing, so the `?? updated` fallback can't hide the failure.
     */
    lastReportedAt?: Date;
    wrangler?: WranglerPlacement;
    poweredOff?: Date | boolean;
    /** Why the walt last powered off (a `PoweredOffReason` from current walts); any string is accepted. Cleared when it next reports powered on. */
    poweredOffReason?: string | null;
    ip?: string;
    /** A Date that the walt's wifi will be disabled until */
    wifiDisabledUntil?: Date;

    /**
     * Set when a play-sound request is not acknowledged in time (device unreachable), forcing the
     * device status to "No Connection". Cleared whenever the walt next reports (any self-update).
     */
    unreachable?: Date | null;

    logRequest?: LogRequest; // pending request for logs when the walt is connected
    facilityId?: string;
    /**
     * The userId of the user who last changed the facility
     * Used to report to Control center for activity tracking
     */
    assignedByUserId?: string;
    dateAssignedToSite?: Date;

    latestFacilityExit?: Date;
    badge?: BadgeLocationData;
    meshRepeater?: MeshRepeater;
    speakerMicFirmwareVersion?: string;
    speakerMicSerialNumber?: string;
    craft?: string;
}

export interface WaltBulkUpdateRequest {
    waltIds: string[];
    facilityId?: string;
    craft?: string | null;
}

export interface WaltSpeakerMicInfo {
    id?: string;
    speakerMicFirmwareVersion?: string;
    lastUpdated?: Date;
    lastWaltId?: string;
    updated?: Date;
}

export interface Walt extends WaltUpdate {
    facilitySettings?: FacilitySettings['walt'];
    waltSettings?: WaltSettings;
    updated?: Date;
    language?: i18nLocale;
    locale?: i18nLocale;
    simWorkaround?: boolean;

    /**
     * Walt credential used for authentication of WaltService requests.
     * This is a credential locally stored on the device and within the expires collection.
     * This credential is updated when a user logs into the walt.
     * This credential is salted and hashed before being sent to the server.
     */
    waltCredential?: string;

    /**
     * Set while a repeater-power-loss alert is outstanding for this walt, and cleared once the battery
     * shows it is charging again. Server-side only - deliberately not on WaltUpdate so a device cannot
     * report it, and so it never passes through waltValidator.
     *
     * `null` is a clear-on-write signal rather than a value that is ever stored
     */
    repeaterPowerAlerted?: Date | null;
}

export interface AssignWaltFacilityRequest {
    facilityId: string;
}

export interface WaltResponse extends Walt {
    accountName?: string;
    facilityName?: string;
    simWorkaround?: boolean;
    /** The assigned account's enabled actions; only on the assignment response, where walt offline login reads them. */
    accountEnabledActions?: string[];
}

export enum WaltRemoteLogoffReason {
    badge = 'badge',
    console = 'console',
    deleted = 'deleted',
    reassigned = 'reassigned',
    singleWaltLogin = 'single-walt-login',
    waltCredentialInvalidated = 'walt-credential-invalidated',
}

/**
 * Request to clear/disable/enable wifi networks from a Walt
 * waltId is required if accountId/facilityId is not provided
 * accountId is required if facilityId is provided
 * facilityId is required if accountId is provided
 */
export interface WaltWifisRequest {
    waltId?: string;
    accountId?: string;
    facilityId?: string;
}

export interface WaltEnableDisableWifisRequest extends WaltWifisRequest {
    disabled: boolean;
}

export enum WaltModel {
    walt1 = 'walt1',
    walt2 = 'walt2',
}

export function getWaltModel(waltId: string): WaltModel | null {
    const upperWaltId = waltId?.toUpperCase();
    if (upperWaltId >= 'WT00' && upperWaltId < 'WT02') {
        return WaltModel.walt1;
    } else if (upperWaltId >= 'WT02' && upperWaltId < 'WT03') {
        return WaltModel.walt2;
    }
    return null;
}

/** A login/logout or login/dock session for a walt device. */
export interface WaltSession {
    id: string;
    personId?: string;
    personName?: string;
    loginDate: Date;
    /**
     * How the session ended:
     * - `WaltLogout` user explicitly logged out
     * - `WaltDocked` unit returned to wrangler
     * - `WaltPoweredOff` unit powered off while signed in, for the reason in `endReason`
     * - `undefined` session is still active
     */
    endType?: EventType.WaltLogout | EventType.WaltDocked | EventType.WaltPoweredOff;
    endDate?: Date;
    /** Why the walt powered off, when `endType` is `WaltPoweredOff`; any string is accepted. */
    endReason?: string;
}

export interface WaltSessionsResponse {
    sessions: WaltSession[];
    /** ISO date string to pass as `before` on the next request. Absent when there are no more pages. */
    nextCursor?: string;
}
